import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { FiUploadCloud, FiTrash2, FiDownload, FiCheck, FiMoon, FiSun, FiLoader, FiClock, FiList } from 'react-icons/fi';
import { AiOutlineFileImage } from 'react-icons/ai';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getOrCreateUserId } from '../utils/uuid';
import { v4 as uuidv4 } from 'uuid';
import { useDarkMode } from '../context/DarkModeContext';
import { log, error, warn } from '../utils/logger';
import { conversionColorMapping, defaultTheme } from '../constants/colorMapping';
import SlideOutPanel from './SlideOutPanel';
import ConversionHistory from './ConversionHistory';
import { showToast } from '../utils/toast';

// ActionButton component matching FileUpload
const ActionButton = ({ icon, children, onClick, variant = "default" }) => {
  const { darkMode } = useDarkMode();
  const currentTheme = conversionColorMapping.imagesToPdf || defaultTheme;
  
  const getThemeColor = () => {
    return currentTheme?.dragActive?.text || 'text-primary-500';
  };

  const baseClasses = "group flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 text-sm font-medium";
  
  const variants = {
    default: `
      ${darkMode
        ? 'bg-gray-900/90 border border-gray-700 text-neutral-100 shadow-sm hover:bg-gray-800 hover:border-purple-500 focus:ring-2 focus:ring-purple-500 focus:outline-none'
        : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-200'}
    `,
    ghost: `
      ${darkMode
        ? 'bg-transparent text-neutral-400 hover:text-purple-400 hover:bg-gray-800/60 focus:ring-2 focus:ring-purple-500 focus:outline-none'
        : 'bg-transparent hover:bg-gray-100/50 text-gray-600'}
    `
  };

  return (
    <button
      onClick={onClick}
      className={`
        ${baseClasses} 
        ${variants[variant]}
        hover:shadow-sm
        active:scale-95
        transform transition-all duration-200
      `}
    >
      <span className={`
        inline-flex transition-all duration-200 transform
        group-hover:scale-110 group-hover:rotate-[-10deg]
        group-hover:${getThemeColor()}
        ${darkMode ? 'text-purple-400' : ''}
      `}>
        {icon}
      </span>
      <span className={`
        transition-colors duration-200
        group-hover:${getThemeColor()}
      `}>
        {children}
      </span>
    </button>
  );
};

const ImagesToPdf = () => {
  const [files, setFiles] = useState([]);
  const [conversionHistory, setConversionHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const { darkMode, toggleDarkMode } = useDarkMode();
  const [conversionInProgress, setConversionInProgress] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [downloadingFiles, setDownloadingFiles] = useState({});

  const currentTheme = conversionColorMapping.imagesToPdf || defaultTheme;

  useEffect(() => {
    const userId = getOrCreateUserId();
    const storedHistory = JSON.parse(localStorage.getItem(`conversionHistory_${userId}`)) || [];
    setConversionHistory(storedHistory);
  }, []);

  const clearConversionHistory = () => {
    const userId = getOrCreateUserId();
    setConversionHistory([]);
    localStorage.removeItem(`conversionHistory_${userId}`);
    showToast.success('Conversion history cleared.');
  };

  const onDrop = useCallback((acceptedFiles) => {
    const newFiles = acceptedFiles.map(file => {
      const id = Date.now() + Math.random();
      const preview = URL.createObjectURL(file);
      return {
        file,
        progress: 0,
        converted: false,
        id,
        conversionType: 'imagesToPdf',
        preview
      };
    });
    
    setFiles((prevFiles) => [...prevFiles, ...newFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, accept: 'image/*' });

  const handleUpload = async () => {
    if (files.length === 0) {
      toast.error('Please select at least one image to convert.');
      return;
    }
    setConversionInProgress(prev => ({ ...prev, uploading: true }));
    let token = null;

    try {
      const currentUserId = getOrCreateUserId();
      const uniqueFolderId = uuidv4();
      const folderPath = `${currentUserId}/${uniqueFolderId}/`;
      
      log(`Preparing to upload files to folder: ${folderPath}`);

      const uploadedFiles = [];

      // Upload each file
      for (const fileObj of files) {
        log(`Uploading file: ${fileObj.file.name} for userId: ${currentUserId}`);
        setConversionInProgress(prev => ({ ...prev, [fileObj.id]: true }));
        log('Requesting signed URL for upload:', fileObj.file.name);

        const contentType = fileObj.file.type || 'application/octet-stream';

        const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/getSignedUrl`, {
          fileName: `${folderPath}${fileObj.file.name}`,
          userId: currentUserId,
          action: 'write',
          contentType,
          conversionType: 'imagesToPdf'
        });

        const { signedUrl, publicUrl, token: newToken, contentType: returnedContentType } = response.data;
        token = newToken; // Use the last token for the conversion process
        log(`Received token from server: ${token}`);
        log('Received signed URL:', signedUrl);
        log('Content-Type:', returnedContentType);

        await axios.put(signedUrl, fileObj.file, {
          headers: {
            'Content-Type': returnedContentType,
          },
          maxContentLength: Infinity,
          maxBodyLength: Infinity
        });

        log('File uploaded to GCS:', publicUrl);
        uploadedFiles.push(fileObj.file.name);
      }

      log('All files uploaded. Sending conversion request.');
      log('Folder path:', folderPath);
      log('Uploaded files:', uploadedFiles);

      const conversionResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/conversion/other`,
        {
          folderPath: folderPath,
          fileNames: uploadedFiles,
          conversionType: 'imagesToPdf',
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        }
      );

      log('Conversion response:', conversionResponse.data);

      setFiles(prevFiles => 
        prevFiles.map(file => ({ 
          ...file, 
          converted: true, 
          convertedUrl: conversionResponse.data.url,
          convertedFileName: conversionResponse.data.filename,
          token: token
        }))
      );

      const newHistoryItem = {
        fileName: 'Combined Images', 
        conversionType: 'imagesToPdf', 
        timestamp: new Date().toLocaleString(),
        token: token
      };
      setConversionHistory(prev => {
        const updatedHistory = [...prev, newHistoryItem];
        localStorage.setItem(`conversionHistory_${currentUserId}`, JSON.stringify(updatedHistory));
        return updatedHistory;
      });

      toast.success('Images converted to PDF successfully!');
    } catch (error) {
      error('Error converting files:', error);
      if (error.response) {
        error('Response data:', error.response.data);
        error('Response status:', error.response.status);
        error('Response headers:', error.response.headers);
      } else if (error.request) {
        error('No response received:', error.request);
      } else {
        error('Error setting up request:', error.message);
      }
      toast.error(`Error converting files: ${error.message}`);
    } finally {
      setConversionInProgress(prev => ({ ...prev, uploading: false }));
      setConversionInProgress({});
    }
  };

  const handleDownload = async (url, fileName, token) => {
    log(`Preparing to download file: ${fileName} with token: ${token}`);
    try {
      log('Sending verification request for token:', token);
      const verificationResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/conversion/verifyToken`,
        { token },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      log('Token verification response:', verificationResponse.data);

      if (verificationResponse.data.valid) {
        log('Token verified successfully. Proceeding with download.');
        log('Starting file download');
        const response = await axios.get(url, {
          responseType: 'blob',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const blob = new Blob([response.data]);
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        log('File download completed');

        log('Download completed, finalizing token');
        const finalizationResponse = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/conversion/finalizeToken`,
          { token },
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        log('Token finalization response:', finalizationResponse.data);

        // Verify file deletion
        await verifyFileDeletion(token);

        log('Token finalized, updating file status');
        setFiles(prevFiles => 
          prevFiles.map(file => 
            file.token === token ? { ...file, status: 'completed' } : file
          )
        );

        toast.success(`${fileName} downloaded successfully!`);
      } else {
        error('Token verification failed');
        toast.error(`Error downloading ${fileName}: Invalid token`);
      }
    } catch (error) {
      error('Error during download process:', error);
      if (error.response) {
        error('Error response:', error.response.data);
        error('Error status:', error.response.status);
      }
      toast.error(`Error downloading ${fileName}: ${error.message}`);
    }
  };

  const verifyFileDeletion = async (token) => {
    try {
      log(`Verifying file deletion for token: ${token}`);
      const verificationResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/conversion/verifyDeletion/${token}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      log('File deletion verification response:', verificationResponse.data);
      
      if (verificationResponse.data.deletionSuccessful) {
        log('Files successfully deleted');
        toast.success('Files have been successfully deleted from the server.');
      } else {
        warn('File deletion may have failed. Details:', verificationResponse.data.details);
        toast.warn('File deletion may have been incomplete. Please check the console for details.');
      }
    } catch (error) {
      error('Error verifying file deletion:', error);
      if (error.response) {
        error('Error response:', error.response.data);
        error('Error status:', error.response.status);
      }
      toast.error(`Error verifying file deletion: ${error.message}`);
    }
  };

  const handleRemoveFile = (id) => {
    const fileToRemove = files.find(f => f.id === id);
    if (fileToRemove?.preview) {
      URL.revokeObjectURL(fileToRemove.preview);
    }
    setFiles(files.filter(file => file.id !== id));
  };

  // Cleanup previews on unmount
  useEffect(() => {
    return () => {
      files.forEach(file => {
        if (file.preview) {
          URL.revokeObjectURL(file.preview);
        }
      });
    };
  }, []);

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(files);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setFiles(items);
  };

  const location = useLocation();
  const baseUrl = 'https://iconvertify.com';
  // CRITICAL FIX: Canonical URL must ALWAYS match the current page URL
  const currentPath = location.pathname;
  const canonicalUrl = `${baseUrl}${currentPath}`;
  const pageTitle = 'Convert Images to PDF | Free Online JPG, PNG to PDF Converter - iconvertify';
  const description = 'Convert multiple images (JPG, PNG, GIF, etc.) to a single PDF file online. Free, secure, and fast image to PDF conversion. No registration required.';
  const keywords = 'images to pdf, jpg to pdf, png to pdf, convert images to pdf, image converter, pdf maker, multiple images to pdf';

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <link rel="canonical" href={canonicalUrl} />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        
        {/* OpenGraph */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={`${baseUrl}/favicon-assets/android-chrome-512x512.png`} />
        <meta property="og:image:width" content="512" />
        <meta property="og:image:height" content="512" />
        <meta property="og:image:alt" content="Convert Images to PDF - iconvertify" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="iconvertify" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={`${baseUrl}/favicon-assets/android-chrome-512x512.png`} />
        <meta name="twitter:image:alt" content="Convert Images to PDF - iconvertify" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Images to PDF Converter - iconvertify",
            "url": canonicalUrl,
            "applicationCategory": "UtilityApplication",
            "operatingSystem": "Web Browser",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "description": description,
            "featureList": [
              "Convert multiple images to PDF",
              "Support for JPG, PNG, GIF, and more",
              "Free conversion",
              "No registration required",
              "Secure file processing"
            ]
          })}
        </script>
      </Helmet>
      <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800 text-white' : 'bg-white'} shadow-md w-full max-w-4xl mx-auto mt-4 transition-colors duration-300`}>
      <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <ActionButton
              icon={<FiList className="w-4 h-4" />}
              onClick={() => setIsModalOpen(false)}
              variant="default"
            >
              View Conversions
            </ActionButton>
            
            <ActionButton
              icon={<FiClock className="w-4 h-4" />}
              onClick={() => setShowHistory(true)}
              variant="ghost"
            >
              History
            </ActionButton>
          </div>

          <ActionButton
            icon={darkMode ? <FiSun className="w-4 h-4" /> : <FiMoon className="w-4 h-4" />}
          onClick={toggleDarkMode}
            variant="ghost"
        >
            {darkMode ? 'Light Mode' : 'Dark Mode'}
          </ActionButton>
      </div>

      <motion.div
        {...getRootProps()}
        className={`
          relative flex flex-col items-center justify-center w-full h-72 p-8
          ${darkMode ? 'bg-gray-900/90' : 'bg-white'}
          rounded-xl cursor-pointer transition-all duration-300 ease-out
          border-2 ${darkMode ? 'border-gray-800' : ''}
          shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)]
          hover:shadow-[0_20px_50px_-12px_rgba(0,0,0,0.15)]
          hover:-translate-y-2 hover:scale-[1.01]
          ${darkMode ? 'bg-gradient-to-b from-gray-900/90 to-gray-950/80' : 'bg-gradient-to-b from-white to-gray-50/50'}
          active:scale-[0.99] active:shadow-[0_10px_40px_-12px_rgba(0,0,0,0.12)] active:translate-y-0
          ${isDragActive
            ? `border-solid ${currentTheme.dragActive.border} ${currentTheme.dragActive.background}`
            : `border-dashed ${darkMode ? 'border-gray-700' : 'border-gray-200'} hover:border-solid hover:${currentTheme.dragActive.border}`
          }
        `}
          animate={{
            scale: isDragActive ? 1.02 : 1,
            y: isDragActive ? -8 : 0,
          }}
          whileHover={{
            y: -4,
            transition: { duration: 0.2 }
          }}
          transition={{
            type: "spring",
            stiffness: 260,
            damping: 20
          }}
      >
        <input {...getInputProps()} />
          
          {/* Sophisticated gradient background effect */}
          <div className={`absolute inset-0 rounded-xl transition-all duration-300 pointer-events-none
            ${darkMode 
              ? 'bg-gradient-to-br from-purple-900/20 via-transparent to-purple-800/10 opacity-0 group-hover:opacity-100' 
              : 'bg-gradient-to-br from-white via-purple-50/20 to-purple-100/30 opacity-0 group-hover:opacity-100'
            }`} />
          
          {/* Enhanced border glow with harmonious colors */}
          <div className={`absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100
                    transition-opacity duration-300 pointer-events-none
                    ${darkMode 
                      ? 'bg-gradient-to-r from-purple-500/0 via-purple-400/15 to-purple-500/0' 
                      : 'bg-gradient-to-r from-purple-200/0 via-purple-300/12 to-purple-200/0'
                    }
                    blur-xl`} />
          
          {/* Subtle inner glow for depth */}
          {isDragActive && (
            <div className={`absolute inset-[2px] rounded-xl pointer-events-none
              ${darkMode 
                ? 'bg-gradient-to-br from-purple-600/10 via-purple-500/5 to-transparent' 
                : 'bg-gradient-to-br from-purple-100/20 via-purple-50/10 to-transparent'
              }`} />
          )}

          <motion.div
            className="relative z-10 flex flex-col items-center gap-4"
            animate={{ 
              y: isDragActive ? -8 : 0,
              scale: isDragActive ? 1.1 : 1
            }}
            transition={{ 
              type: "spring", 
              stiffness: 300, 
              damping: 20 
            }}
          >
            <FiUploadCloud 
              className={`h-16 w-16 transition-all duration-300
                ${isDragActive 
                  ? currentTheme?.dragActive?.text || 'text-purple-500'
                  : currentTheme?.icon || 'text-gray-400'
                }
                drop-shadow-md hover:drop-shadow-xl
                ${darkMode ? 'drop-shadow-[0_2px_16px_rgba(147,51,234,0.15)]' : ''}`}
            />
            
            <p className={`
              text-lg text-center font-medium
              transition-all duration-300
              ${isDragActive 
                ? currentTheme.dragActive.text
                : darkMode ? 'text-neutral-100' : 'text-gray-600'
              }
              drop-shadow-md
              ${darkMode ? 'drop-shadow-[0_2px_8px_rgba(147,51,234,0.10)]' : ''}`}
            >
          {isDragActive ? "Drop the images here" : "Drag & drop images here, or click to select"}
        </p>
          </motion.div>
          
          {/* Dark mode overlay */}
          {darkMode && (
            <div className="pointer-events-none absolute inset-0 rounded-xl bg-gradient-to-br from-gray-900/80 via-gray-950/60 to-gray-900/90 opacity-80 z-0" />
          )}

          {/* Enhanced animated particles with harmonious colors */}
          {isDragActive && (
            <motion.div
              className="absolute inset-0 pointer-events-none overflow-hidden rounded-xl"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {/* Floating particles */}
              <div className="absolute inset-0">
                {[...Array(8)].map((_, i) => (
                  <motion.div
                    key={i}
                    className={`absolute w-2 h-2 rounded-full
                      ${darkMode ? 'bg-purple-400/40' : 'bg-purple-500/30'}
                      shadow-lg
                      ${darkMode ? 'shadow-purple-500/30' : 'shadow-purple-400/20'}`}
                    animate={{
                      y: [-25, 25, -25],
                      x: [Math.sin(i) * 15, Math.cos(i) * 15, Math.sin(i) * 15],
                      opacity: [0.4, 0.9, 0.4],
                      scale: [1, 1.3, 1],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: i * 0.25,
                    }}
                    style={{
                      left: `${15 + (i % 4) * 25}%`,
                      top: `${25 + Math.floor(i / 4) * 30}%`,
                    }}
                  />
                ))}
              </div>
              
              {/* Ripple effect */}
              <motion.div
                className={`absolute inset-0 rounded-xl
                  ${darkMode 
                    ? 'bg-gradient-radial from-purple-500/10 via-purple-400/5 to-transparent' 
                    : 'bg-gradient-radial from-purple-300/15 via-purple-200/8 to-transparent'
                  }`}
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.3, 0.6, 0.3],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
            </motion.div>
          )}
      </motion.div>

      {files.length > 0 && (
        <div className="mt-6 space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-neutral-100' : 'text-gray-900'}`}>
                Images to Convert ({files.length})
              </h3>
              <p className={`text-xs mt-1 ${darkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                Drag to reorder • Images will be combined in this order
              </p>
            </div>
          </div>

          <div className="overflow-x-auto">
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="fileList">
              {(provided) => (
                  <ul {...provided.droppableProps} ref={provided.innerRef} className="space-y-2 min-w-max">
                  <AnimatePresence>
                    {files.map((fileObj, index) => (
                      <Draggable key={fileObj.id} draggableId={fileObj.id.toString()} index={index}>
                        {(provided) => (
                          <motion.li
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            initial={{ opacity: 0, y: 50 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -50 }}
                              className={`flex items-center justify-between p-3 rounded-lg transition-all duration-200
                                ${darkMode 
                                  ? 'bg-gradient-to-r from-gray-800/90 to-gray-800/50 backdrop-blur-sm border border-gray-700/50 hover:from-gray-800/95 hover:to-gray-800/60' 
                                  : 'bg-gradient-to-r from-white/95 to-white/80 backdrop-blur-sm border border-gray-200/50 hover:from-white hover:to-white/90'
                                }
                                shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)] hover:shadow-[0_8px_25px_-5px_rgba(0,0,0,0.1)]`}
                            >
                              <div className="flex items-center space-x-3 w-1/2 min-w-[200px] flex-shrink-0">
                                {/* Image Preview Thumbnail */}
                                <div className="flex-shrink-0 relative group">
                                  <div className="w-12 h-12 rounded-lg overflow-hidden border-2 border-purple-200 dark:border-purple-700 bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/30 dark:to-purple-800/30">
                                    {fileObj.preview ? (
                                      <img 
                                        src={fileObj.preview} 
                                        alt={fileObj.file.name}
                                        className="w-full h-full object-cover"
                                      />
                                    ) : (
                                      <AiOutlineFileImage className="h-full w-full p-2 text-purple-500" />
                                    )}
                                  </div>
                                  {/* Hover preview */}
                                  <div className="absolute left-0 top-14 z-50 hidden group-hover:block pointer-events-none">
                                    <div className={`w-48 h-48 rounded-lg overflow-hidden border-2 ${darkMode ? 'border-purple-700' : 'border-purple-300'} shadow-xl bg-white dark:bg-gray-800`}>
                                      {fileObj.preview && (
                                        <img 
                                          src={fileObj.preview} 
                                          alt={fileObj.file.name}
                                          className="w-full h-full object-contain"
                                        />
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex flex-col flex-grow min-w-0">
                                  <span 
                                    className={`font-medium truncate text-sm ${darkMode ? 'text-neutral-100' : 'text-gray-900'}`}
                                    title={fileObj.file.name}
                                  >
                                    {fileObj.file.name}
                                  </span>
                                  <span className={`text-xs ${darkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                                    {fileObj.file.size ? `${(fileObj.file.size / 1024).toFixed(1)} KB` : ''} • {index + 1} of {files.length}
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2 flex-grow justify-end">
                                {/* Page number indicator */}
                                <div className={`flex items-center justify-center w-8 h-8 rounded-full text-xs font-medium
                                  ${darkMode 
                                    ? 'bg-purple-900/50 text-purple-300 border border-purple-700' 
                                    : 'bg-purple-100 text-purple-700 border border-purple-200'
                                  }`}>
                                  {index + 1}
                                </div>
                                {conversionInProgress[fileObj.id] ? (
                                  <FiLoader className="animate-spin text-purple-500" />
                                ) : fileObj.converted ? (
                                  <FiCheck className="text-emerald-500" />
                                ) : null}
                                <button
                                onClick={() => handleRemoveFile(fileObj.id)}
                                  className="p-1.5 w-8 h-7 flex items-center justify-center rounded-md transition-all duration-200
                                    bg-rose-500 text-white hover:bg-rose-600
                                    shadow-[0_1px_2px_rgba(0,0,0,0.05)]
                                    hover:shadow-[0_2px_4px_rgba(0,0,0,0.05)]
                                    active:transform active:scale-[0.98]"
                                >
                                  <FiTrash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </motion.li>
                        )}
                      </Draggable>
                    ))}
                  </AnimatePresence>
                  {provided.placeholder}
                </ul>
              )}
            </Droppable>
          </DragDropContext>
          </div>
        </div>
      )}

      <motion.button
        onClick={handleUpload}
        whileHover={files.length > 0 && !conversionInProgress.uploading ? { scale: 1.02 } : {}}
        whileTap={files.length > 0 && !conversionInProgress.uploading ? { scale: 0.98 } : {}}
        className={`mt-6 w-full p-3.5 rounded-xl focus:outline-none transition-all duration-200
          relative overflow-hidden font-medium
          ${files.length === 0 || conversionInProgress.uploading
            ? 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed text-white' 
            : `bg-gradient-to-r from-purple-600 via-purple-500 to-purple-600 
               hover:from-purple-700 hover:via-purple-600 hover:to-purple-700 
               text-white 
               shadow-[0_4px_14px_-2px_rgba(147,51,234,0.4)] 
               hover:shadow-[0_8px_20px_-2px_rgba(147,51,234,0.5)]
               ${darkMode ? 'shadow-purple-900/40' : 'shadow-purple-500/30'}`
          }
        `}
        disabled={files.length === 0 || conversionInProgress.uploading}
      >
        {/* Sophisticated button glow effect */}
        {files.length > 0 && !conversionInProgress.uploading && (
          <div className="absolute inset-0 bg-gradient-to-r from-purple-400/0 via-purple-300/20 to-purple-400/0 
                        opacity-0 hover:opacity-100 transition-opacity duration-300 blur-xl" />
        )}
        <div className="flex items-center justify-center gap-2 relative z-10">
          {conversionInProgress.uploading ? (
            <>
              <FiLoader className="animate-spin w-5 h-5" />
              <span>Converting...</span>
            </>
          ) : (
            <>
              <FiCheck className="w-5 h-5" />
              <span className="font-semibold">Convert to PDF</span>
            </>
          )}
        </div>
      </motion.button>

      {files.some(file => file.converted) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6 space-y-3"
        >
          <h3 className={`text-lg font-semibold ${darkMode ? 'text-neutral-100' : 'text-gray-900'}`}>
            Converted PDF
          </h3>
          <div className={`p-4 rounded-lg shadow-sm flex items-center justify-between transition-all duration-200
            ${darkMode 
              ? 'bg-gradient-to-r from-gray-800/90 to-gray-800/50 border border-gray-700/50' 
              : 'bg-gradient-to-r from-white/95 to-white/80 border border-gray-200/50'
            }
            shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)] hover:shadow-[0_8px_25px_-5px_rgba(0,0,0,0.1)]`}
          >
            <span className={`font-medium ${darkMode ? 'text-neutral-100' : 'text-gray-900'}`}>
              Combined PDF
            </span>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                const convertedFile = files.find(f => f.converted);
                if (convertedFile) {
                  setDownloadingFiles(prev => ({ ...prev, [convertedFile.id]: true }));
                  handleDownload(convertedFile.convertedUrl, convertedFile.convertedFileName, convertedFile.token);
                }
              }}
              disabled={downloadingFiles[files.find(f => f.converted)?.id]}
              className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-center transition-all duration-200
                ${downloadingFiles[files.find(f => f.converted)?.id]
                  ? 'bg-emerald-500/80 text-white cursor-not-allowed'
                  : 'bg-emerald-500 hover:bg-emerald-600 text-white'
                }
                shadow-[0_2px_8px_-2px_rgba(16,185,129,0.3)] hover:shadow-[0_4px_12px_-2px_rgba(16,185,129,0.4)]`}
            >
              {downloadingFiles[files.find(f => f.converted)?.id] ? (
                <FiLoader className="animate-spin w-4 h-4" />
              ) : (
                <FiDownload className="w-4 h-4" />
              )}
              <span className="font-medium">Download</span>
            </motion.button>
          </div>
        </motion.div>
      )}

      <SlideOutPanel isOpen={showHistory} onClose={() => setShowHistory(false)} darkMode={darkMode}>
        <ConversionHistory
          history={conversionHistory}
          darkMode={darkMode}
          clearHistory={clearConversionHistory}
        />
      </SlideOutPanel>

      <ToastContainer
        position="bottom-right"
        autoClose={5000}
        hideProgressBar={true}
        newestOnTop={true}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable={false}
        pauseOnHover
        theme={darkMode ? 'dark' : 'light'}
        className="!-translate-y-4"
        limit={5}
        closeButton={false}
      />
    </div>
    </>
  );
};

export default ImagesToPdf;